import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CaretDown } from "@phosphor-icons/react";
import {
  InboxFolderButton,
  InboxFolderCount,
  InboxFolderMenu,
  InboxFolderOption,
  InboxFolderWrap,
} from "../InboxStyled";

/**
 * Outlook-style mail folders for the inbox list. "All mail" plus the
 * claim-file filing types that actually have items (Agency Decisions,
 * Attorneys Fees, Correspondence, …). Counts come from the list
 * payload so the picker still shows every pile while the list is
 * drilled into one folder.
 */
const InboxFolderFilter = ({
  category = null,
  counts = [],
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const total = counts.reduce((sum, row) => sum + (row.count || 0), 0);
  const active = category
    ? counts.find((row) => row.name === category)
    : null;
  const label = active ? active.name : "All mail";
  const labelCount = active ? active.count : total;

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handlePick = useCallback(
    (nextCategory) => {
      setOpen(false);
      onSelect(nextCategory);
    },
    [onSelect]
  );

  if (!counts.length && !category) return null;

  return (
    <InboxFolderWrap ref={wrapRef}>
      <InboxFolderButton
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        $active={Boolean(category)}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{label}</span>
        {labelCount > 0 && (
          <InboxFolderCount>{labelCount.toLocaleString()}</InboxFolderCount>
        )}
        <CaretDown size={11} weight="bold" />
      </InboxFolderButton>
      {open && (
        <InboxFolderMenu role="listbox" aria-label="Mail folders">
          <InboxFolderOption
            type="button"
            role="option"
            aria-selected={!category}
            $active={!category}
            onClick={() => handlePick(null)}
          >
            <span>All mail</span>
            <InboxFolderCount>{total.toLocaleString()}</InboxFolderCount>
          </InboxFolderOption>
          {counts.map((row) => (
            <InboxFolderOption
              key={row.name}
              type="button"
              role="option"
              aria-selected={category === row.name}
              $active={category === row.name}
              onClick={() => handlePick(row.name)}
            >
              <span>{row.name}</span>
              <InboxFolderCount>{row.count.toLocaleString()}</InboxFolderCount>
            </InboxFolderOption>
          ))}
        </InboxFolderMenu>
      )}
    </InboxFolderWrap>
  );
};

InboxFolderFilter.propTypes = {
  category: PropTypes.string,
  counts: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ),
  onSelect: PropTypes.func.isRequired,
};

export default InboxFolderFilter;
